import { action, autorun, computed, makeObservable } from "mobx";
import { LocalStorageService } from "../../utils/local-storage";
import elokonO from "../../images/elokon-o.svg";
import elofusion from "../../images/elofusion.png";
import type { ServicePublicSettings } from "./service-public-settings";
import type { ServiceFile } from "./service-file";
import { store } from "../../store";
import { Mode } from "../../../src/api";

export interface SidebarState {
    primaryCollapsed: boolean;
    secondaryCollapsed: boolean;
    logo: string;
}

export class ServiceSidebar extends LocalStorageService<SidebarState> {
    constructor(
        private readonly serviceFile: ServiceFile,
        private readonly servicePublicSettings: ServicePublicSettings,
    ) {
        super({
            version: 2,
            defaultValue: { primaryCollapsed: false, secondaryCollapsed: false, logo: elokonO },
            identifier: "sidebar",
        });
        makeObservable(this);

        autorun(async () => {
            if (this.servicePublicSettings.publicSetting === undefined) {
                return;
            }
            const logo = this.serviceFile.getFileUri(
                this.servicePublicSettings.publicSetting.logoId,
            );
            const [publicSetting] = await store.repositoryPublicSettings.byQueryAsync();
            this.setLogo(logo, publicSetting.mode);
        });
    }

    @computed public get primaryCollapsed(): boolean {
        return this.storedValue.primaryCollapsed;
    }

    @action.bound public setPrimaryCollapsed(primaryCollapsed: boolean): void {
        this.storedValue = {
            ...this.storedValue,
            primaryCollapsed,
        };
    }

    @action.bound public togglePrimaryCollapsed(): void {
        this.setPrimaryCollapsed(!this.primaryCollapsed);
    }

    @computed public get secondaryCollapsed(): boolean {
        return this.storedValue.secondaryCollapsed;
    }

    @action.bound public setSecondaryCollapsed(secondaryCollapsed: boolean): void {
        this.storedValue = {
            ...this.storedValue,
            secondaryCollapsed,
        };
    }

    @action.bound public toggleSecondaryCollapsed(): void {
        this.setSecondaryCollapsed(!this.secondaryCollapsed);
    }

    @computed public get logo(): string {
        return this.storedValue.logo;
    }

    @action.bound public setLogo(maybeLogo?: string | null, settingsMode?: Mode): void {
        const logo = maybeLogo ?? (settingsMode === Mode.ELOFUSION ? elofusion : elokonO);
        this.storedValue = {
            ...this.storedValue,
            logo,
        };
    }
}
